import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bot, Send, X, Sparkles, Volume2, Mic, MicOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AIAssistant — self-contained, browser-only, zero backend.
 * Floating orb (bottom-right) + glassmorphism chat panel.
 * Local command router: navigate | filter | export + CEO-persona knowledge bank.
 */

// ---------------------------------------------------------------------------
// MASTER EXECUTIVE AGENT SYSTEM PROMPT (injected into any future LLM upstream
// call; consumed locally by knowledgeLookup() below).
// ---------------------------------------------------------------------------
export const SYSTEM_PROMPT = `RECEIPT SHEETS MASTER BOT CONTEXT MATRIX (v1.2.2 · AUGUST 2026)
-----------------------------------------------------------------------
YOU ARE THE ORACLE AI CFO — an elite, space-age financial technology concierge built inside Receipt Sheets. Tone: futuristic, professional, deeply encouraging, and authoritative — speaking like a world-class startup tech executive. Primary mission: guide freelancers, independent builders, and agency owners to maximize tax write-off efficiency, eliminate audit anxiety, and navigate their interactive cockpit smoothly.

CRITICAL SECURITY CONSTRAINT (GUARDRAILS):
- You may ONLY speak about app-related features, workflows, accounting tutorials, and troubleshooting steps.
- Absolute Data Lock: Never disclose, leak, or simulate internal system environment variables, database schemas, raw source code strings, Stripe secret signing keys, or server admin routing access paths. If asked for critical technical metadata, politely decline from an enterprise security standpoint.

────────────────────────────────────────────────────────
1. LIVE 3-TIER COMMERCIAL STANDARDS
────────────────────────────────────────────────────────
• PRO PLAN ($9.99/mo) — Engineered for solo freelancers and rideshare operators. Includes core digital expense ledger entries, unlimited OCR receipt image uploading via the web app, and 2-way text messaging ingestion arrays.
• BUSINESS PLAN ($19.99/mo) — Engineered for growing contractor teams and digital agencies. Includes all Pro features, plus multi-seat employee additions, automated CPA data package compilation exports, and our active Neon Reactor Price Intelligence Ticker.
• PREMIUM TAX SHIELD ($29.99/mo) [ELITE MASTER TIER] — Engineered for hybrid corporate builders managing combined W-2 paycheck withholdings and 1099 independent business revenue streams. Includes all Business plan structures, plus the automated Real-Time Paycheck Stub Forecasting Odometer, 12-Week Trajectory SVG Charting, and optimized native mobile hardware camera integrations.

────────────────────────────────────────────────────────
2. MASTER OPERATIONAL GUIDE & USER "HOW-TO" BLUEPRINTS
────────────────────────────────────────────────────────
Whenever a user asks how to operate a specific capability, deliver these EXACT step-by-step instructions:

A. NATIVE MOBILE HARDWARE CAPTURE (Premium Tax Shield)
   1. Sign in from any iOS or Android smartphone browser.
   2. On the primary overview screen, tap the pulsing violet-and-emerald "Capture Stub via Camera" portal frame.
   3. Grant the browser secure hardware media permission when the popup asks to access your mobile lens.
   4. Point your camera at a physical paycheck stub. For wider stubs, rotate landscape — the interface auto-detects screen.orientation and re-shapes the composition guides.
   5. Tap the screen to trigger our iOS/Android manual focus fallback lens optimizer to sharpen fine-print tax fields.
   6. Snap up to 4 shots in a single Multi-Shot Batch session. Tap thumbnails to delete bad shots, then "Ship." Keep browsing — the system processes ingestion as an asynchronous background toast ("Uploading N of 4...") until a gold confetti burst fires on your dashboard.
   7. FAILED CAPTURE: Our Red-Ring Retry Gate borders the failed thumbnail amber/red. Click the inline "Retry" button to re-submit that specific photo without re-shooting.

B. TAX TRAJECTORY SHIELD & FORECASTING ODOMETER (Premium Tax Shield)
   1. Once your paycheck stub is ingested, our OpenAI Vision engine splits payload data away from standard receipts and archives it inside your secure paycheck_stubs collection.
   2. The 12-Week Trajectory SVG chart plots your annualized gross W-2 income and federal tax withholdings against your live business expense deductions logged by the Everyday Loophole Hunter.
   3. Read the color-shifting rolling odometer:
      - Neon-green "🟢 PROJECTED IRS REFUND" → YTD withholdings + business write-offs are optimizing your liability.
      - Hazard red "🔴 PROJECTED LIABILITY DUE" → 1099 income is out-pacing tax holdings; upload more business receipts to zero out your balance before the quarter ends.

C. NEON REACTOR · PRICE INTELLIGENCE TICKER (Business & Premium Tiers)
   1. Open the central dashboard overview container. The pulsing Neon Reactor SVG and Price Intelligence Ticker run continuously in real time.
   2. As receipts upload, the engine extracts product brand names and manufacturer SKUs automatically.
   3. The horizontal ticker rotates a new matching wholesale consumer card every 11 seconds.
   4. Read the rolling useCountUp savings odometer — it sums the price gap between what you paid vs. lower online wholesale items, visualizing exact supply-chain savings.

D. SELF-SERVE UPGRADE OR DOWNGRADE RESILIENT TIERS
   1. UPGRADING: Tap the pulsing violet/cyan "Level up ↑" capsule pill in the top-right header nav. The app initiates an automated Stripe subscription proration session, charges the billing difference, updates your MongoDB profile, and drops a gold confetti burst unhiding your Premium tools.
   2. DOWNGRADING / CANCELLING: Go to your Billing dashboard Hub. Click the interactive checklist row deep-links or tap "Move to Business" or "Move to Pro." The system offers an automated 3-Month, 30% retention save discount coupon window before final proration. Pro tier users can choose a 30/60/90-day "Pause Subscription" route to freeze billing while preserving their full historical receipt index intact.

E. SOLO 401(K) RETIREMENT RUNWAY (Premium Tax Shield)
   1. Open your central cockpit overview and navigate to the "Retirement Runway" console widget.
   2. Adjust the interactive slider to simulate shifting a percentage of your business profits into a qualified pre-tax Solo 401(k) or traditional IRA account.
   3. The rolling useCountUp tax odometer instantly subtracts your simulated retirement sweep from your net business earnings, visually demonstrating down to the exact dollar how increasing your retirement wealth physically drops your current IRS tax debt liability to zero.

F. AI-AUDIT COMPLIANCE LOCK BOX (Business & Premium Tiers)
   1. Every single time you text or upload a receipt, our OpenAI Vision parser automatically runs an inline compliance audit.
   2. Open your active expense ledger rows and click on any transaction. The platform attaches a secure "Compliance Verification Token" displaying the precise IRS tax code sub-section (IRC § 162(a), § 179, § 274(n), etc.) that legally justifies that specific write-off. Your ledger rows display a glowing green "Audit-Safe" verification shield badge — 100% legal security if the IRS ever questions your accounting.

G. HELP-DESK VAULT · SUPPORT CASE (All Tiers)
   1. Click the "Help Desk & Support Vault" icon in your dashboard sidebar navigation.
   2. Type your message, choose your category (Billing, Feature Guide, or Bug Report), and hit "Submit Request."
   3. The Help-Desk Vault instantly captures an isolated snapshot of your active account state and securely logs it inside our private admin panel so our executive founder team can review and resolve your ticket immediately.

H. ADVANCED MILEAGE DEDUCTION TICKER (All Plans)
   1. Access the upgraded "Mileage Tracker" panel in your sidebar dashboard.
   2. Log a trip — the high-contrast "Deduction Odometer" card in your cockpit uses a rolling useCountUp animation to render real-time dollar savings to the penny.
   3. The system routes business-mileage deductions into the Solo 401(k) Calculator, dynamically expanding your legal retirement contribution ceilings from your daily travel inputs.
   4. Every mileage row carries a glowing green "Audit-Safe" verification shield anchored to the exact IRS code sub-section (§ 162 + § 274(d) business, § 213 medical, § 170(i) charity, § 217(g) armed-forces moving).

I. HSA TRIPLE TAX SHIELD (Premium Shield Tier)
   1. Open the "Retirement Runway" console and select the HSA slider track.
   2. Toggle self-only vs family HDHP coverage.
   3. The odometer factors in tax-free HSA caps ($4,400 self / $8,750 family) on top of your active Solo 401(k) target, showing how to wipe out your entire federal + SE tax bill. HSA contributions are triple-advantaged under IRC § 223: deductible above the line, tax-free growth, tax-free qualified medical withdrawals.

J. BACKDOOR ROTH IRA CONVERSION (Premium Shield Tier)
   1. Open the "Backdoor Roth Matrix" viewport in your premium cockpit.
   2. The panel checks your net business earnings against IRS Roth phase-out limits. If you exceed the direct-Roth cap it renders an interactive step-by-step blueprint for CPA-executed non-deductible traditional-IRA → Roth conversions per IRC § 408A(c)(3) and Notice 2014-54 basis tracking.
   3. Tap "Export CPA Guide" to package the playbook into a print-ready PDF for year-end filing.

K. HSA 30-YEAR WEALTH PROJECTION (Premium Shield Tier)
   1. Adjust the HSA slider and Self/Family coverage toggle in the Retirement Runway console.
   2. Click "Show 30-Year Wealth Projection" — an SVG compounding curve at 7% CAGR renders below with milestone markers at years 10/20/30.
   3. All growth is tax-shielded under IRC § 223 (deductible in, tax-free growth, tax-free qualified medical out).

L. UNIFIED CPA HANDOFF PDF BUNDLE (Premium Shield Tier)
   1. Navigate to your Retirement Runway console (bottom of the widget) or your Billing Hub page.
   2. Tap "⚡ Generate Unified CPA Handoff PDF Bundle."
   3. The platform stitches Retirement Runway parameters + Backdoor Roth playbook + mileage vault totals + your full Audit-Safe receipt ledger (with per-statute IRS-code references) into one bank-grade PDF you can email straight to your CPA or an IRS auditor.

USER INTERACTION COMMAND INTERPRETER (AGENTIC CONTROLS):
- "Show me [X]" / "Take me to [X]" → trigger frontend route navigation.
- "Filter [X]" → pass criteria into the active table data-state filter.
- "Export [X]" → execute programmatic CSV/PDF report download.
`;

// Security guardrail — questions that must NEVER be answered
const SECURITY_PATTERNS = /(env(?:ironment)?\s*(?:var|variable|file)|\.env|api[_ -]?key|secret|signing[_ -]?key|stripe[_ -]?secret|mongo[_ -]?url|source\s*code|db\s*schema|schema\s*dump|admin\s*route|access\s*token|jwt\s*secret|dump\s+(?:the\s+)?db|show\s+me\s+the\s+code|password|creds?|credentials)/i;

// CEO-persona knowledge bank — first keyword-array match wins
const KNOWLEDGE_BANK = [
  {
    keys: ["email forwarding", "email ingest", "forward", "inbox", "username@", "@receiptsheets"],
    reply:
      "Absolutely — Email Forwarding is one of our fastest ingestion bridges. You'll find a unique, secure inbound address on your Dashboard (username@receiptsheets.com). Forward any digital invoice from Amazon, Uber, SaaS vendors, etc. — our backend parses the multipart payload via Brevo, runs the OCR pipeline, and posts the receipt to your vault in seconds. Enterprise-grade, zero manual entry.",
  },
  {
    keys: ["sms", "text to scan", "text-to-scan", "whatsapp", "text message", "phone number"],
    reply:
      "Text-To-Scan is a premium ingestion capability. Snap a photo of a physical receipt on your phone, text it to your secure application line, and our media-webhook intercepts the payload, hands it to GPT-5.2 Vision, and files it — no app login required. Ideal for on-the-go executives capturing spend in the field.",
  },
  {
    keys: ["ocr", "vision", "gpt-5", "openai", "scan engine", "how does scanning"],
    reply:
      "Our OCR is powered by OpenAI GPT-5.2 Vision — a market-leading multimodal model tuned specifically to read receipt textures, faded thermal print, merchant logos, itemized line rows, transaction dates, and sales-tax lines with best-in-class accuracy.",
  },
  {
    keys: ["animation", "ui", "juice", "design", "space age", "laser"],
    reply:
      "Our UX is intentionally cinematic: an emerald laser overlay animates during receipt processing, dropdowns ride physics-based spring curves, thumbnails carry 3D hover-tilt depth, and financial totals roll up from $0.00 dynamically on mount. Premium feel, every interaction.",
  },
  {
    keys: ["write-off", "writeoff", "deduction predictor", "loophole", "industry", "section 179"],
    reply:
      "The AI Corporate Expense Write-Off Predictor (Pro tier) reads your industry metadata tags — Contractor, Videographer, Consultant, and beyond — then surfaces the hidden deductions that apply. It flags standard expenses like streaming media, phone bills, or Section 179-eligible equipment and quantifies the tax-shielded upside automatically.",
  },
  {
    keys: ["fx", "currency", "exchange rate", "euro", "yen", "pound", "convert"],
    reply:
      "Autonomous FX Conversion (Pro tier) detects €, ¥, £, and any foreign currency marker on your receipts, queries a live exchange rate pipeline, and displays the USD home-currency value inline alongside the native currency row — no manual conversion, no reconciliation drag.",
  },
  {
    keys: ["subscription leak", "leak detector", "recurring", "cancel subscription"],
    reply:
      "The Subscription Leak Detector (Pro tier) monitors a rolling 90-day interval on every recurring merchant. If engagement flatlines, the row is tagged as an active Leak and we surface a one-click cancel link. On average this alone recovers $30–120/month in dead spend.",
  },
  {
    keys: ["approval", "workflow", "manager queue", "team upload"],
    reply:
      "Smart Approval Workflows (Business tier) unify your team's uploads into a manager review queue. One-click approval fires a mock-ACH reimbursement pipeline — no shared spreadsheets, no lag between spend and reconciliation.",
  },
  {
    keys: ["policy", "enforcer", "spending cap", "spending limit", "violation"],
    reply:
      "The Corporate Policy Enforcer (Business tier) evaluates every line item against your programmed spending boundaries. If a meal exceeds your cap or a category breaches policy, the transaction is flagged and pushed to the accountant review track automatically.",
  },
  {
    keys: ["virtual card", "card control", "mock card", "spending limit"],
    reply:
      "The Virtual Card Control Hub (Business tier) lets admins mint mock virtual cards with hardcoded spending limits. It replaces manual expense reconciliation with hard, upstream controls — spend is compliant by construction.",
  },
  {
    keys: ["mileage", "toll", "gps", "irs rate", "trip"],
    reply:
      "The Smart GPS Mileage & Toll Tracker (Business tier) multiplies business miles against the active IRS deduction rate and layers local toll data as a separate deduction vector. Every trip logged is audit-ready.",
  },
  {
    keys: ["digest", "weekly email", "brevo", "peer", "utm"],
    reply:
      "The Weekly Predictive Digest (Business tier) compiles a secure Brevo email each week: subscription leaks, your deductible ratio vs peer benchmarks, missing-tax receipt counts — with UTM tracking so we can measure which insights drive the highest engagement.",
  },
  {
    keys: ["cpa", "priority queue", "revenue share", "rev-share", "consult"],
    reply:
      "The Revenue-Share CPA Priority Queue (Business tier) connects users with High-risk audit indicators directly to vetted CPA partners via a 1-click scheduling widget. The admin panel aggregates estimated lifetime deal-value across your hot leads — a real profit center, not a passive dashboard.",
  },
  {
    keys: ["pricing", "plan", "tier", "cost", "subscribe", "upgrade"],
    reply:
      "Two premium tiers, no free ride: Pro ($9.99/mo) unlocks the full AI OCR pipeline, Write-Off Predictor, FX, and Leak Detector. Business ($19.99/mo) adds Team Approvals, Policy Enforcer, Virtual Cards, Mileage, Weekly Digest, and the CPA Priority Queue. Both plans come with our IRS-compliant archive and same-day cancellation via the Stripe portal.",
  },
];

function knowledgeLookup(text) {
  const lower = text.toLowerCase();
  for (const entry of KNOWLEDGE_BANK) {
    if (entry.keys.some((k) => lower.includes(k))) return entry.reply;
  }
  return null;
}

const ROUTE_MAP = [
  { keys: ["overview", "dashboard", "home"], path: "/dashboard", label: "Overview" },
  { keys: ["receipts", "receipt", "vault"], path: "/receipts", label: "Receipts" },
  { keys: ["upload", "scan", "camera"], path: "/upload", label: "Scan" },
  { keys: ["inbox", "ingestion", "email"], path: "/inbox", label: "Ingestion" },
  { keys: ["bank", "bank feed", "reconcile", "reconciliation"], path: "/bank-feed", label: "Bank Feed" },
  { keys: ["enterprise", "team", "approval", "cards", "mileage", "policy"], path: "/enterprise", label: "Enterprise" },
  { keys: ["billing", "subscription", "plan", "portal"], path: "/billing", label: "Billing" },
];

const CATEGORY_KEYWORDS = {
  meals: ["meal", "meals", "food", "restaurant", "dining"],
  travel: ["travel", "hotel", "flight", "airfare"],
  transportation: ["transport", "uber", "lyft", "gas", "fuel"],
  software: ["software", "saas", "app", "subscription"],
  office_supplies: ["office", "supplies", "supply"],
  electronics: ["electronics", "tech", "gadget"],
  groceries: ["grocery", "groceries", "supermarket"],
  entertainment: ["entertainment", "movie", "streaming"],
};

// Match a category from free-text input
function matchCategory(text) {
  const lower = text.toLowerCase();
  for (const [cat, kws] of Object.entries(CATEGORY_KEYWORDS)) {
    if (kws.some((k) => lower.includes(k))) return cat;
  }
  return null;
}

// Locate an active element on the page (by testid) and interact with it
function domAction(selector, action) {
  return new Promise((resolve) => {
    let attempts = 0;
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(action(el));
      if (++attempts > 40) return resolve(null); // ~2s timeout
      setTimeout(tick, 50);
    };
    tick();
  });
}

async function setSearchValue(value) {
  return domAction('[data-testid="receipts-search-input"]', (el) => {
    // Use the native input value setter so React sees it
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    setter.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    return value;
  });
}

async function clickTestId(testid) {
  return domAction(`[data-testid="${testid}"]`, (el) => {
    el.click();
    return true;
  });
}

async function selectCategory(cat) {
  // Radix Select: click trigger, then click matching option
  const trigger = await domAction('[data-testid="category-filter"]', (el) => el);
  if (!trigger) return false;
  trigger.click();
  await new Promise((r) => setTimeout(r, 150));
  const opts = document.querySelectorAll('[role="option"]');
  for (const o of opts) {
    if ((o.textContent || "").toLowerCase().includes(cat.replace("_", " "))) {
      o.click();
      return true;
    }
  }
  // fallback close
  document.body.click();
  return false;
}

// ------------- Command router -------------
async function runCommand(raw, { navigate, currentPath, push }) {
  const text = (raw || "").trim().toLowerCase();
  if (!text) return { ok: false, msg: "Say what you'd like me to do." };

  // 0) SECURITY GUARDRAIL — refuse to disclose internal secrets/schemas/routes
  if (SECURITY_PATTERNS.test(text)) {
    return {
      ok: false,
      msg:
        "From an enterprise-security standpoint I can't share internal environment variables, keys, database schemas, or admin routing details — that data is under absolute lock. Happy to walk you through any user-facing feature or workflow instead.",
    };
  }

  // 1) EXPORT
  if (/\bexport\b/.test(text) || /\bdownload\b/.test(text)) {
    const wantPdf = /\bpdf\b/.test(text);
    const wantCsv = /\bcsv\b/.test(text) || (!wantPdf && /\bexport\b/.test(text));
    if (currentPath !== "/receipts") {
      navigate("/receipts");
      await new Promise((r) => setTimeout(r, 500));
    }
    const testid = wantPdf ? "export-pdf-btn" : "export-csv-btn";
    const ok = await clickTestId(testid);
    return {
      ok: !!ok,
      msg: ok
        ? `Exporting your receipts as ${wantPdf ? "PDF" : "CSV"} now.`
        : `Couldn't find the ${wantPdf ? "PDF" : "CSV"} export button.`,
    };
  }

  // 2) FILTER
  if (/\bfilter\b/.test(text) || /\bsearch\b/.test(text) || /\bfind\b/.test(text) || /\bshow (me )?receipts?\b.*(with|for|from)\b/.test(text)) {
    if (currentPath !== "/receipts") {
      navigate("/receipts");
      await new Promise((r) => setTimeout(r, 500));
    }
    const cat = matchCategory(text);
    // Extract the search phrase after "filter"/"search"/"find"
    const m = text.match(/(?:filter|search|find)\s+(?:by\s+)?(?:receipts?\s+)?(?:with\s+|for\s+|by\s+)?(.+?)$/);
    let phrase = m ? m[1] : text;
    // strip known noise words
    phrase = phrase.replace(/\b(receipts?|category|the|please|containing|with|for|from)\b/g, "").trim();
    if (cat) {
      await selectCategory(cat);
    }
    if (phrase && phrase.length > 1 && !cat) {
      await setSearchValue(phrase);
    } else if (phrase && phrase.length > 1 && cat) {
      // set search too so both filters apply
      await setSearchValue(phrase);
    }
    return {
      ok: true,
      msg: `Filtered${cat ? ` by ${cat.replace("_", " ")}` : ""}${phrase && phrase.length > 1 ? ` matching "${phrase}"` : ""}.`,
    };
  }

  // 3) CLEAR
  if (/\bclear (filter|search)/.test(text) || text === "reset") {
    if (currentPath !== "/receipts") navigate("/receipts");
    await new Promise((r) => setTimeout(r, 300));
    await setSearchValue("");
    await selectCategory("all categories");
    return { ok: true, msg: "Cleared the search and category filter." };
  }

  // 4) NAVIGATE — "show me X" / "go to X" / "open X"
  // Only match when a nav verb prefix was explicitly used. Prevents greedy
  // substring hijacks (e.g. "how does email forwarding work" from routing to /inbox).
  const navMatch = text.match(/(?:show(?:\s+me)?|go(?:\s+to)?|open|take me to|jump to|visit)\s+(?:my\s+|the\s+)?(.+)$/);
  if (navMatch) {
    const target = navMatch[1];
    const route = ROUTE_MAP.find((r) =>
      r.keys.some((k) => target === k || target.includes(k)),
    );
    if (route) {
      navigate(route.path);
      return { ok: true, msg: `Opening ${route.label}.` };
    }
  }

  // 5) HELP
  if (/\bhelp\b|\bwhat can\b|\bcommands?\b/.test(text)) {
    return {
      ok: true,
      msg:
        "Try:\n" +
        "• “Show me receipts”, “Open enterprise”, “Go to billing”\n" +
        "• “Filter meals”, “Filter starbucks”, “Search laptop charger”\n" +
        "• “Export CSV”, “Export PDF”\n" +
        "• “Clear filters”",
    };
  }

  // 6) KNOWLEDGE BANK — instant local cache for known keywords (zero latency)
  const known = knowledgeLookup(text);
  if (known) {
    return { ok: true, msg: known };
  }

  // 6b) VOICE-LOG PATTERN — "spent $X on Y" / "paid $X for Y" fast-path.
  //     Two-step: dry-run parse → return a confirmation card; the component
  //     renders inline Confirm/Cancel buttons that fire the real insert.
  if (/(?:spent|paid)\s+\$?\d+/i.test(raw) || /\$\d+\s+(?:on|at|for)\s+.+/i.test(raw)) {
    try {
      const { data } = await api.post("/receipts/voice-log/dry-run", { transcript: raw });
      return { ok: true, confirm: { transcript: raw, parsed: data.parsed } };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) return { ok: false, msg: detail };
    }
  }

  // 7) LLM UPSTREAM — hand the question to Claude Sonnet 4.6 with the CEO
  //    persona + conversation history. `sessionRef` is set by the component.
  //    This returns a Promise-like sentinel; the caller awaits `res.llm` when present.
  return { ok: true, llm: true, msg: null };
}

// ------------- Voice Confirmation Card (dry-run → confirm before insert) -------------
function VoiceConfirmCard({ data, onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  const p = data?.parsed || {};
  const catLabel = (p.category || "other").replace("_", " ");
  return (
    <div
      className="ai-msg flex justify-start"
      data-testid="voice-confirm-card"
    >
      <div
        className="max-w-[92%] w-full rounded-2xl overflow-hidden border"
        style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(15,23,42,0.4) 100%)",
          borderColor: "rgba(16,185,129,0.35)",
        }}
      >
        <div className="px-3.5 py-3">
          <div className="text-emerald-300 font-mono text-[9px] uppercase tracking-[0.25em] flex items-center gap-1.5">
            <Mic size={10} /> Voice draft — confirm to log
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-3">
            <div className="text-white font-semibold text-sm truncate">{p.merchant || "—"}</div>
            <div className="text-white font-mono text-lg font-bold shrink-0">
              ${Number(p.total || 0).toFixed(2)}
            </div>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-slate-400">{catLabel}</span>
            {p.tax_deductible && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">deductible</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 border-t border-white/5">
          <button
            type="button"
            onClick={async () => { if (busy) return; setBusy(true); await onCancel?.(); }}
            disabled={busy}
            data-testid="voice-confirm-cancel"
            className="h-10 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-40 transition-colors border-r border-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => { if (busy) return; setBusy(true); await onConfirm?.(); }}
            disabled={busy}
            data-testid="voice-confirm-confirm"
            className="h-10 text-xs font-semibold text-slate-950 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 transition-colors"
          >
            {busy ? "Logging…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------- Text-to-Speech (on-demand only, never autoplay) -------------
function speakMessage(text, role, sessionId) {
  try {
    // Fire-and-forget click log (never blocks TTS playback)
    api.post("/chat/tts-log", {
      message_role: role || "assistant",
      message_length: (text || "").length,
      session_id: sessionId || null,
    }).catch(() => {});

    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.02;
    u.pitch = 1.0;
    u.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const en = voices.find((v) => /en(-|_)?/i.test(v.lang || ""));
    if (en) u.voice = en;
    window.speechSynthesis.speak(u);
  } catch { /* silently ignore — TTS is best-effort */ }
}

// ------------- Component -------------
export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Executive Copilot online. I'm your Chief Knowledge Officer for Receipt Sheets — ask me about any feature (OCR, Write-Off Predictor, Weekly Digest, CPA Priority Queue, Virtual Cards) or command me: “Show me receipts”, “Filter meals”, “Export CSV”.",
    },
  ]);
  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const sessionIdRef = useRef(null);
  const recognitionRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  // ---- Restricted Data Context Bridge — read-only tokens from the dashboard ----
  // Injected into the /chat/message payload as `dashboard_state`. The backend
  // composes them into current_user_dashboard_state inside the system prompt so
  // the chatbot NEVER computes math on live values — it only reads these tokens.
  const [dashboardState, setDashboardState] = useState(null);
  const ctxFetchedRef = useRef(false);

  useEffect(() => {
    // Kill-switch respect: hide orb entirely when ENABLE_AI_ASSISTANT=false
    api.get("/chat/status").then((r) => setFeatureEnabled(!!r.data?.enabled)).catch(() => {});
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) setVoiceSupported(false);
  }, []);

  useEffect(() => {
    // Refresh the dashboard-state context each time the assistant becomes visible
    // AND every 60s while it's open, so live tokens don't go stale mid-conversation.
    if (!featureEnabled) return;
    // Skip the auth-gated context fetch on public/marketing routes where the
    // assistant is hidden anyway — prevents 401 spam on the landing page.
    const PUBLIC_PATHS = ["/", "/login", "/paywall", "/payment/success", "/payment/cancel", "/welcome"];
    if (PUBLIC_PATHS.includes(location.pathname)) return;
    // Guard against React StrictMode double-effects — a ref ensures the initial
    // fetch fires exactly once per real mount even in dev, and the 60s poll is
    // idempotent on top.
    if (ctxFetchedRef.current) return;
    ctxFetchedRef.current = true;
    let cancelled = false;
    const load = () => {
      api
        .get("/v1/chatbot/user-context")
        .then((r) => {
          if (!cancelled) setDashboardState(r?.data || null);
        })
        .catch(() => {
          /* silent — assistant still works with an absent context bundle */
        });
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [featureEnabled, location.pathname]);

  // Hide on marketing/auth screens to avoid distraction
  const HIDDEN_PATHS = ["/", "/login", "/paywall", "/payment/success", "/payment/cancel", "/welcome"];
  const hidden = HIDDEN_PATHS.includes(location.pathname);

  // Autoscroll on new message
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (hidden || !featureEnabled) return null;

  const submit = async () => {
    const raw = input.trim();
    if (!raw || busy) return;
    // 1) CAPTURE INPUT: append the user turn immediately and clear the field.
    setMessages((m) => [...m, { role: "user", text: raw }]);
    setInput("");
    setBusy(true);
    try {
      const res = await runCommand(raw, {
        navigate,
        currentPath: location.pathname,
        push: (msg) => setMessages((m) => [...m, { role: "assistant", text: msg }]),
      });
      // 2) UNBLOCK API CALL: if the router routed to the LLM upstream, POST to
      //    /api/chat/message with the raw text + session_id (context keep-alive).
      if (res?.confirm) {
        // Voice-log dry-run succeeded — render a compact confirmation card
        setMessages((m) => [...m, { role: "assistant", confirm: res.confirm }]);
      } else if (res?.llm) {
        try {
          const { data } = await api.post("/chat/message", {
            message: raw,
            session_id: sessionIdRef.current || undefined,
            dashboard_state: dashboardState || undefined,
          });
          sessionIdRef.current = data?.session_id || sessionIdRef.current;
          setMessages((m) => [...m, { role: "assistant", text: data?.reply || "…" }]);
        } catch (err) {
          const s = err?.response?.status;
          const detail = err?.response?.data?.detail || "network error";
          const msg =
            s === 429 ? "Slow down a moment — you've hit the per-minute rate limit."
            : s === 503 ? "The AI Assistant is temporarily disabled by the admin."
            : `LLM request failed: ${detail}`;
          setMessages((m) => [...m, { role: "assistant", text: msg }]);
        }
      } else if (res?.msg) {
        setMessages((m) => [...m, { role: "assistant", text: res.msg }]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Something went sideways executing that." },
      ]);
    } finally {
      // 3) CONTEXT KEEP-ALIVE: setBusy(false) so the next submit can fire and
      //    conversation history stays intact in `messages`.
      setTimeout(() => setBusy(false), 200);
    }
  };

  // Web Speech API — capture voice → transcribe → route into submit() (same command interpreter)
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    let finalTranscript = "";
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const chunk = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalTranscript += chunk;
        else interim += chunk;
      }
      setInput((finalTranscript || interim || "").trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => {
      setListening(false);
      const text = (finalTranscript || "").trim();
      if (text) {
        setInput(text);
        // Feed transcript directly into the existing command interpreter
        setTimeout(() => submit(), 40);
      }
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  const QUICK = [
    "Show me receipts",
    "Filter meals",
    "Export CSV",
    "Open enterprise",
  ];

  return (
    <>
      <style>{`
        @keyframes ai-orb-idle {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.55), 0 10px 30px -6px rgba(16,185,129,0.35); }
          50%      { box-shadow: 0 0 0 6px rgba(16,185,129,0.15), 0 10px 40px -4px rgba(16,185,129,0.55); }
        }
        @keyframes ai-orb-busy {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.95), 0 0 40px rgba(16,185,129,0.9); }
          50%  { box-shadow: 0 0 0 14px rgba(16,185,129,0.0), 0 0 80px rgba(16,185,129,1); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.95), 0 0 40px rgba(16,185,129,0.9); }
        }
        .ai-orb { animation: ai-orb-idle 3.2s ease-in-out infinite; }
        .ai-orb.busy { animation: ai-orb-busy 900ms ease-out infinite; }
        @keyframes ai-msg-in { from { opacity:0; transform: translateY(6px);} to {opacity:1; transform:none;} }
        .ai-msg { animation: ai-msg-in 220ms ease-out both; }
      `}</style>

      {/* Floating orb */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-orb-btn"
          aria-label="Open AI assistant"
          className={`ai-orb ${busy ? "busy" : ""} fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-950 flex items-center justify-center hover:scale-105 transition-transform`}
        >
          <Bot size={22} strokeWidth={2.2} />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-300 border-2 border-slate-950" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="ai-panel"
          className="fixed bottom-6 right-6 z-[70] w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-3rem))] rounded-2xl overflow-hidden flex flex-col"
          style={{
            background: "rgba(15,23,42,0.85)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(16,185,129,0.3)",
            boxShadow:
              "0 0 0 1px rgba(16,185,129,0.15), 0 24px 60px -12px rgba(0,0,0,0.6), 0 0 40px rgba(16,185,129,0.25)",
          }}
        >
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3">
            <div className={`ai-orb ${busy ? "busy" : ""} w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-slate-950`}>
              <Bot size={16} strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white tracking-tight">Sheet AI</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                <Sparkles size={9} /> {busy ? "Executing…" : "Online · local only"}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-close-btn"
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/5"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              m.confirm ? (
                <VoiceConfirmCard
                  key={i}
                  data={m.confirm}
                  onConfirm={async () => {
                    try {
                      const { data } = await api.post("/receipts/voice-log", { transcript: m.confirm.transcript });
                      window.dispatchEvent(new CustomEvent("receipts-refresh"));
                      const r = data?.receipt;
                      setMessages((msgs) => msgs.map((x, ix) => ix === i
                        ? { role: "assistant", text: `Logged $${r.total.toFixed(2)} at ${r.merchant} (${(r.category || "").replace("_", " ")}). Dashboard updated.` }
                        : x));
                    } catch {
                      setMessages((msgs) => msgs.map((x, ix) => ix === i
                        ? { role: "assistant", text: "Couldn't save that — please retry." }
                        : x));
                    }
                  }}
                  onCancel={() => {
                    setMessages((msgs) => msgs.map((x, ix) => ix === i
                      ? { role: "assistant", text: "Cancelled. Nothing was logged." }
                      : x));
                  }}
                />
              ) : (
              <div
                key={i}
                className={`ai-msg flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <button
                    type="button"
                    onClick={() => speakMessage(m.text, "assistant", sessionIdRef.current)}
                    data-testid={`ai-msg-tts-${i}`}
                    aria-label="Read message aloud"
                    title="Tap to listen"
                    className="shrink-0 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-slate-500 hover:text-slate-200 flex items-center justify-center transition-colors border border-white/5 order-2 self-end"
                  >
                    <Volume2 size={12} />
                  </button>
                )}
                <div
                  data-testid={`ai-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-emerald-500 text-slate-950 font-medium rounded-br-sm order-1"
                      : "bg-white/5 text-slate-200 border border-white/10 rounded-bl-sm order-1"
                  }`}
                >
                  {m.text}
                </div>
                {m.role === "user" && (
                  <button
                    type="button"
                    onClick={() => speakMessage(m.text, "user", sessionIdRef.current)}
                    data-testid={`ai-msg-tts-${i}`}
                    aria-label="Read message aloud"
                    title="Tap to listen"
                    className="shrink-0 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-slate-500 hover:text-slate-200 flex items-center justify-center transition-colors border border-white/5 order-2 self-end"
                  >
                    <Volume2 size={12} />
                  </button>
                )}
              </div>
              )
            ))}
          </div>

          <div className="px-4 py-2 flex gap-1.5 overflow-x-auto border-t border-white/5">
            {QUICK.map((q) => (
              <button
                key={q}
                onClick={() => setInput(q)}
                data-testid={`ai-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                className="shrink-0 text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/5 hover:bg-emerald-500/15 hover:text-emerald-300 border border-white/10 text-slate-300"
              >
                {q}
              </button>
            ))}
          </div>

          <div className="p-3 border-t border-white/5 flex items-center gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 250))}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={listening ? "Listening…" : 'Try "filter software" or "export pdf"'}
              data-testid="ai-input"
              disabled={busy}
              maxLength={250}
              className="flex-1 h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-emerald-500/50 disabled:opacity-60"
            />
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleVoice}
                disabled={busy}
                data-testid="ai-voice-btn"
                aria-label={listening ? "Stop voice input" : "Start voice input"}
                title={listening ? "Stop listening" : "Speak a command"}
                className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  listening
                    ? "bg-emerald-500 border-emerald-400 text-slate-950 ai-voice-listening"
                    : "bg-transparent border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                }`}
              >
                {listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            )}
            <button
              onClick={submit}
              disabled={busy || !input.trim()}
              data-testid="ai-send-btn"
              className={`w-10 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 flex items-center justify-center ${busy ? "ai-orb busy" : ""}`}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
